/* eslint-disable no-unused-vars */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Stack } from '@mui/material';
import { Box } from '@mui/system';
import { DESCRIPTION_RENDER_STYLE } from '../style';

/**
 * DescriptionRenderer
 *
 * Renders a description string with keywords highlighted.
 * Clickable keywords call `onKeywordClick`.
 *
 * @param {string} props.description - The full description text
 * @param {Array<{keyword: string}>} props.keywords - List of keywords to highlight
 * @param {function} [props.onKeywordClick] - Callback when a keyword is clicked
 * @returns {JSX.Element} Description with clickable keyword highlights
 */
const DescriptionRenderer = ({ description, keywords, onKeywordClick }) => {
  if (!keywords.length) return <span>{description}</span>;
  //
  const keywordStrings = keywords.map((k) => k.keyword);
  // Regex to match any keyword (case-insensitive) in the description
  const regex = new RegExp(`(${keywordStrings.join('|')})`, 'gi');
  // Split the description by the keywords, keeping the keywords in the array
  const parts = description.split(regex)?.filter((e) => e !== undefined);
  //
  return (
    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.5} mt={2}>
      {parts.map((part) => {
        const matched = keywords.find((k) => k.keyword.toLowerCase() === part.toLowerCase());
        if (!matched) {
          return <span key={part}>{part}</span>;
        }
        //
        return (
          <Box
            key={part}
            sx={DESCRIPTION_RENDER_STYLE}
            onClick={(event) => onKeywordClick?.(event, matched)}
          >
            {part}
          </Box>
        );
      })}
    </Stack>
  );
};

export default DescriptionRenderer;
